import { WorldFC } from 'components/World';
import { useInitPage } from 'hooks/useInitPage';
import { Filters } from './components/FiltersLayout';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { featuredHotspotIds, allHotspotIds, hotspotIds, ALWAYS_VISIBLE_HOTSPOT_IDS, typeFilters, DEFAULT_ORIENTATION } from './constants';
import { emitFilterHotspots } from 'utils';

// Helper to always include the ALWAYS_VISIBLE_HOTSPOT_IDS hotspots in filter results
const addAlwaysVisibleHotspot = (ids) => {
  if (!ids) return ALWAYS_VISIBLE_HOTSPOT_IDS;
  if (Array.isArray(ids)) {
    const allIds = [...ALWAYS_VISIBLE_HOTSPOT_IDS, ...ids];
    // Remove duplicates
    return [...new Set(allIds)];
  }
  return ids;
};

const TongQuanPage = (props) => {
  useInitPage(props);

  // Get worldParams from Redux to get the updated sceneId after useInitPage effect

  const currentPano = useSelector((state) => state.currentPano);

  const [currentType, setCurrentType] = useState('tien-ich');
  const [currentAmenitiesFilters, setCurrentAmenitiesFilters] = useState([]);
  const [currentTrafficFilters, setCurrentTrafficFilters] = useState([]);
  const [isShowAllMode, setIsShowAllMode] = useState(false);

  // Get all filter IDs for the current type
  const allFilterIds = typeFilters[currentType]?.map(f => f.id) || [];

  // Current filters based on type
  const currentFilters = currentType === 'nganh-nghe' ? currentAmenitiesFilters : currentTrafficFilters;

  // Track if user has interacted with filters beyond initial state
  const [hasInteractedWithFilters, setHasInteractedWithFilters] = useState(false);

  // Show featured hotspots on initial load (with small delay to ensure subscription is ready)
  useEffect(() => {
    const timer = setTimeout(() => {
      emitFilterHotspots(featuredHotspotIds);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Handle showing all hotspots (toggle)
  const handleShowAll = () => {
    if (isShowAllMode) {
      // Already in show all mode - uncheck all
      setIsShowAllMode(false);
      setHasInteractedWithFilters(false);
      setCurrentAmenitiesFilters([]);
      setCurrentTrafficFilters([]);
      emitFilterHotspots(featuredHotspotIds);
    } else {
      // Not in show all mode - check all
      setIsShowAllMode(true);
      setHasInteractedWithFilters(false);
      // Set all filter IDs as active (all checked)
      setCurrentAmenitiesFilters(allFilterIds);
      setCurrentTrafficFilters(allFilterIds);
      emitFilterHotspots(allHotspotIds);
    }
  };

  const handleShowAllWithFocus = () => {
    if (typeof window !== 'undefined' && typeof window.focusTo === 'function') {
      window.isFocusToCall = true;
      window.focusTo(...DEFAULT_ORIENTATION);
    }
    handleShowAll();
  };

  // Get hotspot IDs for given filter IDs
  //eslint-disable-next-line
  const getHotspotIdsForFilters = (filterIds) => {
    const ids = [];
    (filterIds || []).forEach(filterId => {
      const hotspots = hotspotIds[filterId] || [];
      ids.push(...hotspots);
    });
    return ids;
  };

  const callFocusToDefault = () => {
    if (typeof window !== 'undefined' && typeof window.focusTo === 'function') {
      window.isFocusToCall = true;
      window.focusTo(...DEFAULT_ORIENTATION);
    }
  };

  // Handle filter toggle
  const handleToggleFilter = (filterId) => {

    if (isShowAllMode) {
      // In show all mode: clicking a filter should select only that one filter
      const setter = currentType === 'nganh-nghe' ? setCurrentAmenitiesFilters : setCurrentTrafficFilters;
      setIsShowAllMode(false);
      setter([filterId]);
      const ids = hotspotIds[filterId] || [];
      emitFilterHotspots(addAlwaysVisibleHotspot(ids));
    } else {
      // Normal mode: clicking a filter toggles it
      const isCurrentlyActive = currentFilters.includes(filterId);

      if (isCurrentlyActive) {
        // Uncheck - go back to default orientation
        callFocusToDefault();
        setHasInteractedWithFilters(false);
        const setter = currentType === 'nganh-nghe' ? setCurrentAmenitiesFilters : setCurrentTrafficFilters;
        setter([]);
        emitFilterHotspots(featuredHotspotIds);
      } else {
        // Check this filter
        setHasInteractedWithFilters(true);
        const setter = currentType === 'nganh-nghe' ? setCurrentAmenitiesFilters : setCurrentTrafficFilters;
        setter([filterId]);
        const ids = hotspotIds[filterId] || [];
        emitFilterHotspots(addAlwaysVisibleHotspot(ids));
      }
    }
  };

  // Handle filter selection (single select)
  const handleChangeTrafficFilters = (filterId) => {
    // Ignore undefined filterId
    if (filterId === undefined) {
      return;
    }
    setIsShowAllMode(false);
    if (!filterId) {
      callFocusToDefault();
    }
    setCurrentTrafficFilters(filterId ? [filterId] : []);
    setHasInteractedWithFilters(true);

    if (filterId) {
      const ids = hotspotIds[filterId] || [];
      emitFilterHotspots(addAlwaysVisibleHotspot(ids));
    }
  };

  const handleChangeAmenitiesFilters = (filterId) => {
    // Ignore undefined filterId
    if (filterId === undefined) {
      return;
    }
    setIsShowAllMode(false);
    if (!filterId) {
      callFocusToDefault();
    }
    setCurrentAmenitiesFilters(filterId ? [filterId] : []);
    setHasInteractedWithFilters(true);

    if (filterId) {
      const ids = hotspotIds[filterId] || [];
      emitFilterHotspots(addAlwaysVisibleHotspot(ids));
    }
  };

  // Handle when switching between type tabs - show featured hotspots
  const handleFilterSwitch = (filterId) => {
    setCurrentType(filterId);
    setIsShowAllMode(false);
    setCurrentAmenitiesFilters([]);
    setCurrentTrafficFilters([]);
    setHasInteractedWithFilters(false);
    emitFilterHotspots(featuredHotspotIds);
  };

  // Handle showing featured hotspots (when switching filters)
  const handleShowFeaturedHotspots = () => {
    setIsShowAllMode(false);
    setCurrentAmenitiesFilters([]);
    setCurrentTrafficFilters([]);
    setHasInteractedWithFilters(true);
    emitFilterHotspots(featuredHotspotIds);
  };

  // Render WorldFC with the scene data from Redux
  // The useInitPage hook will set the correct sceneId in worldParams

  return (
    <>
      <WorldFC />
      {currentPano?._id === '68d12cca208f82c724e17eb7' && (
        <Filters
          currentAmenitiesFilters={currentAmenitiesFilters}
          currentTrafficFilters={currentTrafficFilters}
          currentType={currentType}
          onChangeType={handleFilterSwitch}
          onChangeAmenitiesFilters={handleChangeAmenitiesFilters}
          onChangeTrafficFilters={handleChangeTrafficFilters}
          onShowAll={handleShowAll}
          hasInteractedWithFilters={hasInteractedWithFilters}
          onShowFeaturedHotspots={handleShowFeaturedHotspots}
          isShowAllMode={isShowAllMode}
          allFilterIds={allFilterIds}
          onToggleFilter={handleToggleFilter}
          handleShowAllWithFocus={handleShowAllWithFocus}
        />
      )}
    </>
  );
};

export default TongQuanPage;
